import { FilterMenu, FilterMenuField } from "design-system/molecules/FilterMenu"
import { useCampaignsQuery } from "modules/campaigns/hooks/useCampaignsQuery"
import { useFilterBarLabels } from "modules/shared/hooks/useFilterBarLabels"
import { usePredefinedSegmentGroupsTranslation } from "modules/segments/hooks/usePredefinedSegmentGroupsTranslation"
import { usePredefinedSegmentTranslations } from "modules/segments/hooks/usePredefinedSegmentTranslations"
import { useSegmentGroupsQuery } from "modules/segments/hooks/useSegmentGroupsQuery"
import { useTranslation } from "react-i18next"
import { CanonicalTopicFilterBy, ChannelEnum, ConversationFlowEnum } from "schema"
import { z } from "zod"

type FilterBy = z.input<typeof CanonicalTopicFilterBy>["filterBy"]

type Props = {
  filterBy: FilterBy
  onChange: (filterBy: FilterBy) => void
}

export const TreemapsFilters = ({ filterBy, onChange }: Props) => {
  const { t } = useTranslation()
  const labels = useFilterBarLabels()

  const { data: segmentGroups = [] } = useSegmentGroupsQuery({
    onSuccess: (data) => {
      if (!filterBy?.segments) return

      const filterByIsValid = filterBy.segments.every((s) => {
        return data.some(
          (sg) => sg.id === s.groupId && sg.segments.some((segment) => segment.id === s.segmentId),
        )
      })

      if (!filterByIsValid) {
        onChange({ ...filterBy, segments: undefined })
      }
    },
  })

  const { getSegmentGroupTranslation } = usePredefinedSegmentGroupsTranslation()
  const { getSegmentTranslation } = usePredefinedSegmentTranslations()

  const { data: campaigns = [] } = useCampaignsQuery({
    query: {
      total: 100,
      sortBy: { field: "name", order: "asc" },
      filterBy: {
        archived: false,
      },
    },
  })

  function onChangeSegment(groupId: string, segmentId: string | undefined) {
    const segments = (filterBy?.segments ?? []).filter((s) => s.groupId !== groupId)
    if (segmentId) segments.push({ groupId, segmentId })

    onChange({ ...filterBy, segments: segments.length > 0 ? segments : undefined })
  }

  const segmentFields = segmentGroups
    .filter((sg) => sg.segments.length > 0)
    .map((segmentGroup): FilterMenuField => ({
      key: `segmentGroup-${segmentGroup.id}`,
      label: getSegmentGroupTranslation(segmentGroup) || segmentGroup.name,
      value: filterBy?.segments?.find((s) => s.groupId === segmentGroup.id)?.segmentId,
      onChange: (segmentId) => onChangeSegment(segmentGroup.id, segmentId),
      options: segmentGroup.segments.map((segment) => ({
        value: segment.id,
        label: getSegmentTranslation(segment.name, segmentGroup),
      })),
    }))

  const fields: FilterMenuField[] = [
    {
      key: "campaignId",
      label: t("dashboard.filters.campaign"),
      value: filterBy?.campaignId,
      onChange: (campaignId) => onChange({ ...filterBy, campaignId }),
      options: campaigns.map((campaign) => ({
        value: campaign.id,
        label: campaign.name ?? t("campaigns.untitled"),
        className: !campaign.name ? "text-muted-foreground" : undefined,
      })),
    },
    {
      key: "channel",
      label: t("dashboard.filters.channel"),
      value: filterBy?.channel,
      onChange: (value) =>
        onChange({ ...filterBy, channel: z.enum(ChannelEnum).optional().parse(value) }),
      options: Object.values(ChannelEnum).map((channel) => ({
        value: channel,
        label: t(`dashboard.activity.channels.${channel}`),
      })),
    },
    {
      key: "conversationFlow",
      label: t("dashboard.filters.conversationFlow"),
      value: filterBy?.conversationFlow,
      onChange: (value) =>
        onChange({
          ...filterBy,
          conversationFlow: z.enum(ConversationFlowEnum).optional().parse(value),
        }),
      options: Object.values(ConversationFlowEnum).map((conversationFlow) => ({
        value: conversationFlow,
        label: t(`dashboard.filters.conversationFlows.${conversationFlow}`),
      })),
    },
    ...segmentFields,
  ]

  return <FilterMenu fields={fields} labels={labels} />
}
